import { FolderPlus } from "lucide-react";
import { type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

import { CategoryFolderPicker } from "./CategoryFolderPicker";
import type { MenuKit } from "./menuKit";

type Actions = {
  names: string[];
  onCreateBlank: () => void;
  onCreateCategory: (name: string) => void;
};

/**
 * Toolbar New Folder: today's blank folder, or a CaseBlink category name.
 */
export const NewFolderMenu: FC<Actions> = ({
  names,
  onCreateBlank,
  onCreateCategory
}) => (
  <DropdownMenu>
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            aria-label="New folder"
            data-testid="new-folder"
          >
            <FolderPlus className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
      </TooltipTrigger>
      <TooltipContent side="bottom">New Folder</TooltipContent>
    </Tooltip>
    <DropdownMenuContent align="start" data-testid="new-folder-menu">
      <DropdownMenuItem onSelect={onCreateBlank}>New Folder</DropdownMenuItem>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger
          disabled={!names.length}
          data-testid="caseblink-category"
        >
          CaseBlink Category
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="p-0">
          <CategoryFolderPicker names={names} onPick={onCreateCategory} />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </DropdownMenuContent>
  </DropdownMenu>
);

/**
 * Context "New folder" / "New folder inside": the same two choices.
 * `kit` renders them in the three-dot menu; the default is the right-click menu.
 */
export const NewFolderContextItems: FC<
  Actions & { label: string; kit?: MenuKit }
> = ({ label, names, onCreateBlank, onCreateCategory, kit }) => {
  const Item = kit?.Item ?? (ContextMenuItem as unknown as MenuKit["Item"]);
  const Sub = kit?.Sub ?? (ContextMenuSub as unknown as MenuKit["Sub"]);
  const SubTrigger =
    kit?.SubTrigger ??
    (ContextMenuSubTrigger as unknown as MenuKit["SubTrigger"]);
  const SubContent =
    kit?.SubContent ??
    (ContextMenuSubContent as unknown as MenuKit["SubContent"]);
  return (
    <Sub>
      <SubTrigger>{label}</SubTrigger>
      <SubContent className="w-48">
        <Item onSelect={onCreateBlank}>New Folder</Item>
        <Sub>
          <SubTrigger disabled={!names.length}>CaseBlink Category</SubTrigger>
          <SubContent className="p-0">
            <CategoryFolderPicker names={names} onPick={onCreateCategory} />
          </SubContent>
        </Sub>
      </SubContent>
    </Sub>
  );
};

/** New Folder in the "…" menu, when the toolbar has no room for it. */
export const NewFolderMenuItems: FC<Actions> = ({
  names,
  onCreateBlank,
  onCreateCategory
}) => (
  <DropdownMenuSub>
    <DropdownMenuSubTrigger data-testid="case-files-more-new-folder">
      <FolderPlus />
      New Folder
    </DropdownMenuSubTrigger>
    <DropdownMenuSubContent className="w-48">
      <DropdownMenuItem onSelect={onCreateBlank}>New Folder</DropdownMenuItem>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger disabled={!names.length}>
          CaseBlink Category
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="p-0">
          <CategoryFolderPicker names={names} onPick={onCreateCategory} />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </DropdownMenuSubContent>
  </DropdownMenuSub>
);
